import { FC } from "react";
import Image, { ImageProps } from "next/legacy/image";
import { Avatar } from "baseui/avatar";
import {
  FixedMarker,
  NEEDLE_SIZES,
  PINHEAD_SIZES_SHAPES,
} from "baseui/map-marker";
import classNames from "classnames";
import { CustomDriverAvatar } from "../CustomDriverAvatar";
import {
  Theme,
  useTheme,
} from "@src/common/components/ThemeProvider/ThemeProvider";

interface DriverAvatarProps extends Partial<ImageProps> {
  driverPhoto?: string | null | undefined;
  driverName?: string;
  avatarDisplay?: string | undefined;
  size?: number;
  layout?: "fixed" | "fill" | "intrinsic" | "responsive" | undefined;
  className?: string;
  isActive?: boolean;
}

const DriverAvatar: FC<DriverAvatarProps> = (props: DriverAvatarProps) => {
  const {
    driverPhoto,
    driverName,
    avatarDisplay,
    size,
    layout,
    className,
    onClick,
    isActive = false,
  } = props;
  const { effectiveTheme } = useTheme();
  const isDark = effectiveTheme === Theme.DARK;
  const classes = `${classNames({
    "rounded-full flex": true,
    "w-8 h-8": !size,
  })} ${className}`;

  const avatarOverrides = isDark
    ? {
        Root: {
          style: {
            backgroundColor: "rgb(55 65 81)",
          },
        },
        Avatar: {
          style: {
            backgroundColor: "rgb(55 65 81)",
            color: "rgb(243 244 246)",
          },
        },
      }
    : undefined;

  if (driverPhoto) {
    return (
      <div className={classes} onClick={onClick}>
        <Image
          src={driverPhoto}
          alt="Driver photo"
          width={size || 32}
          height={size || 32}
          objectFit="cover"
          layout={layout}
          className="rounded-full"
        />
      </div>
    );
  }

  if (!driverPhoto && avatarDisplay) {
    if (avatarDisplay.length <= 2) {
      let name = avatarDisplay;
      if (avatarDisplay.length === 2) {
        // we are splitting 2 characters into 2 parts
        // because that is how Avatar component works by assuming this is a full name like John Doe
        name = avatarDisplay.split("").join(" ");
      }
      return (
        <div className={classes} onClick={onClick}>
          <Avatar
            name={name}
            size={size ? `${size}px` : "100%"}
            overrides={avatarOverrides}
          />
        </div>
      );
    } else if (avatarDisplay.length <= 4) {
      // Use custom 3-4 character avatar
      return (
        <div className={classes} onClick={onClick}>
          <CustomDriverAvatar text={avatarDisplay} size={size} />
        </div>
      );
    } else {
      // For more than 4 characters, use FixedMarker as fallback
      return (
        <div className={classes} onClick={onClick}>
          <FixedMarker
            label={avatarDisplay}
            size={PINHEAD_SIZES_SHAPES.small}
            needle={NEEDLE_SIZES.none}
          />
        </div>
      );
    }
  }

  if (!driverPhoto && driverName) {
    return (
      <div className={classes} onClick={onClick}>
        <Avatar
          name={driverName}
          size={size ? `${size}px` : "100%"}
          overrides={avatarOverrides}
        />
      </div>
    );
  }

  return null;
};

export { DriverAvatar };
